import { useEffect, useRef } from "react";
import styles from "./Products.module.css";
import kickdIcon from "../assets/kickd-icon-1024.png";

/* ── Store badge SVGs ── */
function AppleIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
  );
}

function AndroidIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.523 15.341a.863.863 0 01-.862-.862.863.863 0 01.862-.863.863.863 0 01.863.863.863.863 0 01-.863.862zm-11.046 0a.863.863 0 01-.863-.862.863.863 0 01.863-.863.863.863 0 01.862.863.863.863 0 01-.862.862zM17.78 9.19l1.732-3.002a.36.36 0 00-.132-.492.36.36 0 00-.493.132l-1.752 3.036A10.63 10.63 0 0012 7.982c-1.847 0-3.581.472-5.135 1.303L5.113 6.249a.361.361 0 00-.493-.132.36.36 0 00-.132.492L6.22 9.19C3.666 10.573 1.945 13.116 1.836 16.078h20.328c-.109-2.962-1.83-5.505-4.384-6.888z"/>
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10"/>
      <line x1="2" y1="12" x2="22" y2="12"/>
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  );
}

function PhoneMockup() {
  return (
    <div className={styles.phoneMockup} aria-hidden="true">
      <div className={styles.phoneNotch} />

      <div className={styles.phoneScreen}>
        {/* Status bar */}
        <div className={styles.statusBar}>
          <span>9:41</span>
          <div className={styles.statusIcons}>
            <span>●●●</span>
            <span>WiFi</span>
            <span>🔋</span>
          </div>
        </div>

        {/* App header */}
        <div className={styles.appHeader}>
          <span className={styles.appLogo}>
            <img src={kickdIcon} className={styles.appLogoIcon} alt="" />
            <span>Kickd</span>
          </span>
          <span className={styles.appBadge}>GAMEDAY</span>
        </div>

        {/* Squad section */}
        <div className={styles.screenSection}>
          <p className={styles.screenLabel}>Today's Squad</p>
          <div className={styles.playerList}>
            {["J. Smith", "M. Nguyen", "T. Williams", "A. Patel"].map((p, i) => (
              <div key={p} className={styles.playerRow}>
                <div className={styles.playerAvatar} style={{
                  background: `hsl(${140 + i * 18}, 60%, 38%)`
                }}>
                  {p[0]}
                </div>
                <span className={styles.playerName}>{p}</span>
                <span className={styles.playerPos}>{["GK","DEF","MID","FWD"][i]}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Vote section */}
        <div className={styles.voteSection}>
          <p className={styles.voteSectionLabel}>⭐ Player of the Match</p>
          <div className={styles.voteBar}>
            <div className={styles.voteBarFill} />
          </div>
          <p className={styles.voteCount}>12 votes cast</p>
        </div>
      </div>

      {/* Floating badges */}
      <div className={`${styles.floatingBadge} ${styles.badge1}`}>
        <span className={styles.badgeEmoji}>📊</span>
        <span className={styles.badgeText}>Live Votes</span>
      </div>
      <div className={`${styles.floatingBadge} ${styles.badge2}`}>
        <span className={styles.badgeEmoji}>✅</span>
        <span className={styles.badgeText}>Lineup Set</span>
      </div>
    </div>
  );
}

export default function Products() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.12 }
    );
    sectionRef.current?.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="products" ref={sectionRef} className={styles.section}>
      <div className={styles.header}>
        <p className={`${styles.eyebrow} reveal`}>// Our Products</p>
        <h2 className={`${styles.heading} reveal`}>
          What we've{" "}
          <span className={styles.accent}>shipped</span>
        </h2>
      </div>

      {/* Kickd product card */}
      <div className={`${styles.productCard} reveal`}>
        {/* Glow */}
        <div className={styles.cardGlow} aria-hidden="true" />

        {/* Left: info */}
        <div className={styles.productInfo}>
          <div className={styles.productMeta}>
            <div className={styles.productLogo}>
              <img src={kickdIcon} className={styles.logoIcon} alt="Kickd logo" />
            </div>
            <div>
              <h3 className={styles.productName}>Kickd</h3>
              <p className={styles.productByline}>by Carrcraft Studio</p>
            </div>
          </div>

          <p className={styles.productDesc}>
            The all-in-one soccer gameday companion. Kickd gives coaches
            powerful squad and lineup management tools while letting parents
            vote on player of the match — all in real time.
          </p>

          <div className={styles.featureList}>
            {[
              "Coach squad & lineup management",
              "Real-time parent voting",
              "Gameday timers & notifications",
              "Match history & stats",
            ].map((f) => (
              <div key={f} className={styles.featureItem}>
                <span className={styles.featureDot} />
                <span>{f}</span>
              </div>
            ))}
          </div>

          <div className={styles.techPill}>
            <span>Built with React Native</span>
            <AppleIcon />
            <AndroidIcon />
          </div>

          <div className={styles.storeButtons}>
            {/* Website */}
            <a
              href="https://www.kickd.au"
              target="_blank"
              rel="noopener noreferrer"
              className={`${styles.storeBtn} ${styles.storeBtnPrimary}`}
              aria-label="Visit Kickd website"
            >
              <GlobeIcon />
              <span>
                <span className={styles.storeBtnSub}>Visit</span>
                <span className={styles.storeBtnMain}>kickd.au</span>
              </span>
            </a>

            {/* App Store */}
            <a
              href="https://apps.apple.com/au/app/kickd"
              target="_blank"
              rel="noopener noreferrer"
              className={`${styles.storeBtn} ${styles.storeBtnDark}`}
              aria-label="Download Kickd on the App Store"
            >
              <AppleIcon />
              <span>
                <span className={styles.storeBtnSub}>Download on the</span>
                <span className={styles.storeBtnMain}>App Store</span>
              </span>
            </a>

            {/* Google Play */}
            <a
              href="https://play.google.com/store/apps/details?id=au.kickd"
              target="_blank"
              rel="noopener noreferrer"
              className={`${styles.storeBtn} ${styles.storeBtnDark}`}
              aria-label="Get Kickd on Google Play"
            >
              <AndroidIcon />
              <span>
                <span className={styles.storeBtnSub}>Get it on</span>
                <span className={styles.storeBtnMain}>Google Play</span>
              </span>
            </a>
          </div>
        </div>

        {/* Right: phone mockup */}
        <div className={styles.productVisual}>
          <PhoneMockup />
        </div>
      </div>

      {/* More coming */}
      <div className={`${styles.comingSoon} reveal`}>
        <p className={styles.comingSoonText}>
          <span className={styles.comingSoonMono}>// </span>
          More products in development — stay tuned.
        </p>
      </div>
    </section>
  );
}
